import { Box, Grid, TextField } from "@mui/material";
import * as React from "react";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import * as documentService from "../../../models/services/documentService";
import { ISessionState } from "../../../models/session/sessionState";
import { Dialog } from "../../framework/Dialog/Dialog";
import { DialogItemsNav } from "../../framework/Dialog/DialogItemsNav";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { FileAttachment, IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { SaveAttachmentBase64 } from "ui/Scripts/source/models/common/saveAttachmentBase64";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { IDocumentFileEditItem } from "source/models/documentFile/documentFileEditItem";
import { DocumentRemoveItem } from "source/models/document/documentRemoveItem";
import { AttachmentIcon } from "source/components/attachments/AttachmentIcon";
import { IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { FormCheckBox } from "source/components/framework/FormCheckBox";
import { removeConfirmationWord } from "ui/Scripts/source/models/consts";
import { DocumentFolderPath } from "../DocumentFolderPath/DocumentFolderPath";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";

export interface IDocumentFileEditDialogProp {
    sessionState: ISessionState;
    id: string;
    folderId: string;
    companyId: string;
    hasPrevious: boolean;
    hasNext: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onSaved: () => void;
    onClose: () => void;
    onRefreshList: () => Promise<void>;
}

export interface IDocumentFileEditDialogState {
    id: string;
    path: string;
    keywords: IIdTitle[];
    code: string;
    comment: string;
    keywordIds: string[];
    file: IFileAttachment;
    image: string;
    resetImage: boolean;
    item: IDocumentFileEditItem;
    rowId: string;
}

export class DocumentFileEditDialog extends React.Component<IDocumentFileEditDialogProp, IDocumentFileEditDialogState> {
    private orgStateHash: string = "";

    constructor(props: IDocumentFileEditDialogProp) {
        super(props);
        this.state = {
            id: "",
            path: "",
            keywords: [],
            item: null,
            code: "",
            comment: "",
            keywordIds: [],
            file: null,
            image: "",
            resetImage: false,
            rowId: "",
        };
    }

    loadData = async (id) => {
        const props = this.props;
        const result = await AppUtils.callService(() => documentService.getDocumentFileEdit(props.companyId, props.folderId, id), false);
        if (!result) return;
        const item = result.item;
        const state = {
            ...this.state,
            id: item.id,
            path: result.path,
            keywords: result.keywords,
            item,
            code: item.code,
            comment: item.comment,
            keywordIds: item.keywordIds,
            file: null,
            image: item.image,
            resetImage: !item.image,
            rowId: item.rowId
        };
        this.setState(state);
        const saveData = await this.getSaveDataFromState(this.props, state);
        this.orgStateHash = saveData.hash;
    };

    componentDidMount(): void {
        this.loadData(this.props.id);
    }

    async componentDidUpdate(prevProps: IDocumentFileEditDialogProp, prevState: IDocumentFileEditDialogState): Promise<void> {
        const props = this.props;
        const state = this.state;
        if (prevProps.companyId === props.companyId && prevProps.id === props.id) return;
        const cancelResult = await AppUtils.cancel((await this.getSaveDataFromState(props, state)).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await this.saveToDb()) return;
            props.onRefreshList();
        }
        this.loadData(props.id);
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "code") {
            this.setState({ code: value });
        } else if (name === "comment") {
            this.setState({ comment: value });
        }
    };

    handleSetImage = async (file: File) => {
        if (!file) return;
        const attachment = await FileAttachment.fileToFileAttachment(file, 0);
        if (!attachment) return;
        if (!AppUtils.validateFileAttachment(attachment)) return;
        this.setState({ file: attachment, resetImage: false, image: attachment.thumbnail });
    };

    handleRemoveImage = () => {
        this.setState({ file: null, resetImage: true, image: "" });
    };

    getSaveDataFromState = async (props: IDocumentFileEditDialogProp, state: IDocumentFileEditDialogState): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("id", state.item?.id);
        result.append("folderId", props.folderId);
        result.append("companyId", props.companyId);
        result.append("rowId", state.rowId);
        result.append("code", state.code);
        result.append("comment", state.comment);
        result.append("keywordIds", state.keywordIds);
        result.append("resetImage", state.resetImage);
        if (state.file) {
            result.append("attachments", await SaveAttachmentBase64.fromFileAttachments([state.file]));
        }
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.code) {
                AppUtils.showErrorMessage(Translations.NameMustBeDefined);
                return resolve(false);
            }
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<SaveSuccess> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const saveData = await this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => documentService.saveDocumentFileEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        const stateChange = { id: result.id, rowId: result.rowId };
        this.orgStateHash = (await this.getSaveDataFromState(props, { ...state, ...stateChange })).hash;
        this.setState(stateChange);
        return result;
    };

    handleSave = async (closeDialog: boolean) => {
        const result = await this.saveToDb();
        if (!result) return;
        if (!closeDialog) {
            this.loadData(result.id);
            this.props.onSaved();
            return;
        }
        this.props.onClose();
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (state.item?.isNew()) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveDocumentParameter, state.item?.code), false, removeConfirmationWord) === ConfirmationDialogResult.Yes) {
            // Call server
            const removeItem = new DocumentRemoveItem();
            removeItem.id = state.id;
            removeItem.code = state.item?.code;
            removeItem.fileType = state.item?.fileType;
            const result = await AppUtils.callService(() => documentService.removeDocuments(props.companyId, props.folderId, [removeItem]), true);
            if (!result) return;
            props.onClose();
        }
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel((await this.getSaveDataFromState(props, state)).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose();
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut((await this.getSaveDataFromState(props, this.state)).hash, this.orgStateHash,
            async () => !!(await this.saveToDb()),
            props.onRefreshList,
            (newStateHash) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.item) return null;
        const isNew = state.item.isNew();
        return (
            <Dialog
                id="documentFileDialog"
                maxWidth="sm"
                title={isNew ? Translations.AddFile : (Translations.File + ": " + state.item.code)}
                body={
                    <Box display="flex" flexDirection="column">
                        <GridRow>
                            <Grid item sm={12}>
                                <Box display="flex" alignItems="center" sx={{ paddingTop: ".5rem" }}>
                                    <div className="pictureContainer form-group">
                                        <AvatarImage
                                            size={60}
                                            id={state.item.id}
                                            name={state.code}
                                            icon={state.resetImage ? <AttachmentIcon fileType={state.item.fileType} color="#E9F0FA" strokeColor="#848688" size="large" /> : null}
                                            image={state.resetImage ? null : state.image}
                                            editable={true}
                                            tooltip={Translations.CkickToChangeThePicture}
                                            removeTooltip={Translations.RemovePicture}
                                            onChange={this.handleSetImage}
                                            onClear={state.resetImage ? null : this.handleRemoveImage}
                                            onError={AppUtils.showErrorMessage}
                                        />
                                    </div>
                                    <Box sx={{ marginTop: "-46px", paddingLeft: "20px", width: "100%" }}>
                                        <FormField title={Translations.Code} required>
                                            <TextField name="code" title={Translations.Code} inputProps={{ maxLength: 248 }} value={state.code} required={true} onChange={this.handleChange} size="small" fullWidth />
                                            <DocumentFolderPath path={state.path} />
                                        </FormField>
                                    </Box>
                                </Box>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.Description} fullWidth margin="0">
                                    <TextField name="comment" title={Translations.Description} inputProps={{ maxLength: 1000 }} value={state.comment} onChange={this.handleChange} multiline rows={5} size="small" fullWidth />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.Keywords} fullWidth>
                                    <div style={{ width: "100%", display: "flex", flexWrap: "wrap", maxHeight: "120px", overflowY: "auto" }}>
                                        {state.keywords.map((keyword) =>
                                            <FormCheckBox
                                                key={keyword.id}
                                                title={keyword.title}
                                                checked={state.keywordIds.indexOf(keyword.id) > -1}
                                                onChange={(checked: boolean) => {
                                                    const keywordIds = this.state.keywordIds.filter(i => i !== keyword.id);
                                                    if (checked) {
                                                        keywordIds.push(keyword.id);
                                                    }
                                                    this.setState({ keywordIds });
                                                }}
                                            />
                                        )}
                                    </div>
                                </FormField>
                            </Grid>
                        </GridRow>
                    </Box>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={this.handlePrevious}
                            onNext={this.handleNext}
                            createUser={!isNew ? state.item.createUser : null}
                            modifyUser={!isNew ? state.item.modifyUser : null}
                        />
                        <DialogButtons
                            isNew={isNew}
                            onCancel={this.handleCancel}
                            onRemove={!isNew ? this.handleRemove : null}
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}
            />
        );
    }
}